import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { atomWithQuery } from 'jotai-tanstack-query';
import { atom } from 'jotai/index';

import { breakpointBunja } from '@flipster/react/misc/responsive';
import { PathnameScope } from '@flipster/react/providers/route';
import { token } from '@flipster/styled-system/tokens';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { isServer } from '@flipster/utils/env-helper';
import { removeLocalePathname } from '@flipster/utils/pathname';

import { authBunja } from '../auth/state';
import { webviewInfoBunja } from '../webview';
import { getZendeskJwtApi } from './api';

declare global {
  interface Window {
    // oxlint-disable-next-line no-explicit-any
    zE: any;
  }
}

const ZendeskKey = '56488c2f-b92e-4107-9b68-d51da229e1cd';
const MobilePaths = ['/support'];
const DesktopPaths = ['/support', '/user/account', '/history'];
const loadZendeskScriptBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const isLoadedAtom = atom(false);
  const isZendeskLoggedInAtom = atom(false);

  // https://developer.zendesk.com/api-reference/widget-messaging/web/core/
  function zE(...args: unknown[]) {
    if (isServer) return;
    // oxlint-disable-next-line no-console
    if (!window.zE) console.warn('Zendesk is not initialized yet');
    window.zE?.apply(null, args);
  }

  // https://developer.zendesk.com/api-reference/widget-messaging/web/core/#show
  function showMessenger() {
    zE('messenger', 'show');
  }

  // https://developer.zendesk.com/api-reference/widget-messaging/web/core/#hide
  function hideMessenger() {
    zE('messenger', 'hide');
  }

  // https://developer.zendesk.com/api-reference/widget-messaging/web/core/#open
  function openMessenger() {
    zE('messenger', 'open');
  }

  // https://developer.zendesk.com/api-reference/widget-messaging/web/core/#set-locale
  function setLocale(locale: string) {
    zE('messenger:set', 'locale', locale);
  }

  // https://developer.zendesk.com/api-reference/widget-messaging/web/core/#set-zindex
  function setZIndex(zIndex: number) {
    zE('messenger:set', 'zIndex', zIndex);
  }

  // https://developer.zendesk.com/api-reference/widget-messaging/web/authentication/#login
  function zendeskLogin(token: string, loginCallback?: () => void) {
    zE('messenger', 'loginUser', (callback: (token: string) => void) => callback(token), loginCallback);
  }

  // https://developer.zendesk.com/api-reference/widget-messaging/web/authentication/#logout
  function zendeskLogout() {
    zE('messenger', 'logoutUser');
  }

  bunja.effect(() => {
    const script = document.createElement('script');
    script.async = true;
    script.id = 'ze-snippet';
    script.src = `https://static.zdassets.com/ekr/snippet.js?key=${ZendeskKey}`;
    script.onload = () => {
      setLocale('en-us');
      setZIndex(Number(token('zIndex.gnb')));
      hideMessenger();
      store.set(isLoadedAtom, true);
    };
    document.body.appendChild(script);
    return () => {
      script.onload = null;
      document.body.removeChild(script);
    };
  });

  return {
    isLoadedAtom,
    isZendeskLoggedInAtom,
    showMessenger,
    hideMessenger,
    openMessenger,
    zendeskLogin,
    zendeskLogout,
  };
});
loadZendeskScriptBunja.debugLabel = 'loadZendeskScriptBunja';

export const zendeskBunja = bunja(() => {
  const fullPathname = bunja.use(PathnameScope);
  const store = bunja.use(JotaiStoreScope);
  const breakpointBunjaInstance = bunja.use(breakpointBunja);
  const webviewInfoBunjaInstance = bunja.use(webviewInfoBunja);
  const loadZendeskScriptBunjaInstance = bunja.use(loadZendeskScriptBunja);
  const authBunjaInstance = bunja.use(authBunja);
  const { isCookieLoggedInAtom } = authBunjaInstance;
  const { booleanBreakpointAtom } = breakpointBunjaInstance;
  const { webviewAtom } = webviewInfoBunjaInstance;
  const {
    isLoadedAtom,
    showMessenger,
    hideMessenger,
    openMessenger,
    zendeskLogin,
    zendeskLogout,
    isZendeskLoggedInAtom,
  } = loadZendeskScriptBunjaInstance;

  const isDesktopAtom = atom((get) => {
    const booleanBreakpoint = get(booleanBreakpointAtom);
    return booleanBreakpoint.isDesktop;
  });

  const showZendeskAtom = atom((get) => {
    const pathname = removeLocalePathname(fullPathname);
    const isLoaded = get(isLoadedAtom);
    const isDesktop = get(isDesktopAtom);
    const isWebview = get(webviewAtom);
    if (!isLoaded || isWebview) return false;
    if (pathname === '/') return true;
    if (isDesktop) {
      return DesktopPaths.some((p) => pathname.startsWith(p));
    } else {
      return MobilePaths.some((p) => pathname.startsWith(p));
    }
  });

  const getZendeskChatJwtAtom = atomWithQuery((get) => {
    const isLoaded = get(isLoadedAtom);
    const isCookieLoggedIn = get(isCookieLoggedInAtom);

    return {
      queryKey: ['getZendeskChatJwt', 'CHAT', isCookieLoggedIn, isLoaded],
      queryFn: () => getZendeskJwtApi('CHAT'),
      enabled: isCookieLoggedIn && isLoaded,
      staleTime: 0,
    };
  });

  const tokenAtom = atom((get) => {
    const { data } = get(getZendeskChatJwtAtom);
    return data?.data.jwt;
  });

  function zendeskEffect() {
    const isLoaded = store.get(isLoadedAtom);
    if (!isLoaded) return;
    const showZendesk = store.get(showZendeskAtom);
    if (showZendesk) {
      showMessenger();
    } else {
      hideMessenger();
    }
  }

  function zendeskLoginEffect() {
    const isZendeskLoggedIn = store.get(isZendeskLoggedInAtom);
    if (isZendeskLoggedIn) return;

    const token = store.get(tokenAtom);
    if (token) {
      zendeskLogin(token, () => {
        store.set(isZendeskLoggedInAtom, true);
      });
    }
  }

  bunja.effect(() => {
    zendeskEffect();
    zendeskLoginEffect();

    const unsubShowZendeskAtom = store.sub(showZendeskAtom, zendeskEffect);
    const unsubTokenAtom = store.sub(tokenAtom, zendeskLoginEffect);

    return () => {
      unsubShowZendeskAtom();
      unsubTokenAtom();
    };
  });

  return {
    openMessenger,
    zendeskLogout,
  };
});
zendeskBunja.debugLabel = 'zendeskBunja';

export function useZendesk() {
  return useBunja(zendeskBunja);
}
